import { Variables } from '../base';

export interface PatientDetails {
    statement: {
        patient: {
            id: string;
            externalId: string;
            first: string;
            middle: string | null;
            last: string;
            suffix: string | null;
            addresses: Array<{
                lines: string[];
                city: string;
                state: string;
                postal: string;
                country: string;
            }>;
            dob: string;
            sex: string;
            phones: string[];
            emails: string[];
        };
        billingProvider: {
            organization: string;
            addresses: Array<{
                lines: string[];
                city: string;
                state: string;
                postal: string;
                country: string;
            }>;
            email: string;
            phone: string;
            npi?: string;
            ein?: string;
        };
        balanceDue: {
            amount: string;
            currency: string;
        };
    };
    paymentsList: Array<{
        id: string;
        totalAmount: { amount: string; currency: string };
        unallocatedAmount: { amount: string; currency: string };
        allocatedAmount: { amount: string; currency: string };
        externalTransactionId: string;
        paymentDate: string;
        refundAmount: { amount: string; currency: string };
    }>;
    statementsList: Array<{
        id: string;
        type: string;
        date: string;
        description: string;
        charge: string;
        credit: string;
        balance: string;
        diagnosisCodes?: string[];
        renderingProviders?: Array<{
            firstName: string;
            lastName: string;
            npi?: string;
        }>;
        facility?: string;
        billingNpi?: string;
        renderingNpi?: string;
        ein?: string;
        metadata?: any;
    }>;
    claimsList?: Array<{
        id: string;
        claimResponsibilityType?: string;
        fromDos?: string;
        throughDos?: string;
        patientFirst?: string;
        patientLast?: string;
        primaryPayerName?: string;
        chargeAmount?: { amount: string; currency: string };
        allowedAmount?: { amount: string; currency: string };
        payerAmount?: { amount: string; currency: string };
        patientAmount?: { amount: string; currency: string };
    }>;
}

// Function to escape HTML entities to prevent XSS
const escapeHtml = (text: string | undefined | null): string => {
    if (text === null || text === undefined) {
        return '';
    }
    const map: { [key: string]: string } = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
    };
    return String(text).replace(/[&<>"']/g, m => map[m]);
};

// Function to format currency
export const formatCurrency = (amount: string, currency: string = 'USD') => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(num);
};

// Function to format date
export const formatStatementDate = (dateString: string) => {
    // Parse the date string as local date to avoid timezone issues
    // Split the date string and create a local date object
    if (!dateString) {
        return '—';
    }

    try {
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor

        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch (error) {
        return '—';
    }
};

// Function to generate payments table rows
const generatePaymentsTableRows = (paymentsList: PatientDetails['paymentsList']): string => {
    if (paymentsList?.length === 0) {
        return '<tr><td colspan="5" class="muted">No rows found</td></tr>';
    }

    // Sort payments by paymentDate (newest first), then by id for stability
    const sorted = [...paymentsList].sort((a, b) => {
        const aDate = a?.paymentDate || '';
        const bDate = b?.paymentDate || '';

        if (aDate && bDate && aDate !== bDate) {
            return new Date(bDate).getTime() - new Date(aDate).getTime();
        }

        const aId = a?.id || '';
        const bId = b?.id || '';
        return aId.localeCompare(bId);
    });

    let allRows = '';
    let rowIndex = 0;
    let lastPaymentDate = '';

    sorted.forEach((payment, index) => {
        const currentPaymentDate = payment.paymentDate || '';
        const isNewDateGroup = currentPaymentDate !== lastPaymentDate;

        // Add separator when payment date changes (except for first row)
        if (isNewDateGroup && index > 0) {
            allRows += `
        <tr class="date-separator">
          <td colspan="5" class="separator-cell"></td>
        </tr>
      `;
        }

        const isAltRow = rowIndex % 2 === 1;
        const rowClass = isAltRow ? 'row-alt' : '';

        allRows += `
      <tr class="${rowClass}">
        <td>${escapeHtml(formatStatementDate(payment.paymentDate))}</td>
        <td class="right">${formatCurrency(payment?.totalAmount?.amount, payment?.totalAmount?.currency)}</td>
        <td class="right">${formatCurrency(payment?.unallocatedAmount?.amount, payment?.unallocatedAmount?.currency)}</td>
        <td class="right">${formatCurrency(payment?.allocatedAmount?.amount, payment?.allocatedAmount?.currency)}</td>
        <td class="right">${formatCurrency(payment?.refundAmount?.amount, payment?.refundAmount?.currency)}</td>
      </tr>
    `;

        lastPaymentDate = currentPaymentDate;
        rowIndex++;
    });

    // Calculate totals
    let totalTotalAmount = 0;
    let totalUnallocated = 0;
    let totalAllocated = 0;
    let totalRefund = 0;

    paymentsList.forEach(payment => {
        if (payment.totalAmount && payment.totalAmount.amount !== '—') {
            const totalValue =
                typeof payment.totalAmount.amount === 'string'
                    ? payment.totalAmount.amount
                    : String(payment.totalAmount.amount);
            totalTotalAmount += parseFloat(totalValue.replace(/[$,]/g, '')) || 0;
        }
        if (payment.unallocatedAmount && payment.unallocatedAmount.amount !== '—') {
            const unallocatedValue =
                typeof payment.unallocatedAmount.amount === 'string'
                    ? payment.unallocatedAmount.amount
                    : String(payment.unallocatedAmount.amount);
            totalUnallocated += parseFloat(unallocatedValue.replace(/[$,]/g, '')) || 0;
        }
        if (payment.allocatedAmount && payment.allocatedAmount.amount !== '—') {
            const allocatedValue =
                typeof payment.allocatedAmount.amount === 'string'
                    ? payment.allocatedAmount.amount
                    : String(payment.allocatedAmount.amount);
            totalAllocated += parseFloat(allocatedValue.replace(/[$,]/g, '')) || 0;
        }
        if (payment.refundAmount && payment.refundAmount.amount !== '—') {
            const refundValue =
                typeof payment.refundAmount.amount === 'string'
                    ? payment.refundAmount.amount
                    : String(payment.refundAmount.amount);
            totalRefund += parseFloat(refundValue.replace(/[$,]/g, '')) || 0;
        }
    });

    // Add summary row (one cell per column: date label, total amount, unallocated, allocated, refund)
    const summaryRow = `
    <tr class="summary-row">
      <td class="summary-label">Total:</td>
      <td class="right summary-value">${formatCurrency(totalTotalAmount.toString())}</td>
      <td class="right summary-value">${formatCurrency(totalUnallocated.toString())}</td>
      <td class="right summary-value">${formatCurrency(totalAllocated.toString())}</td>
      <td class="right summary-value">${formatCurrency(totalRefund.toString())}</td>
    </tr>
  `;

    return allRows + summaryRow;
};

// Function to format responsibility type
const formatResponsibility = (responsibilityType: string | undefined): string => {
    if (!responsibilityType) {
        return '—';
    }

    // Convert enum value to readable format (e.g., "ENTER" -> "Enter", "PAYER" -> "Payer")
    const formatted = responsibilityType
        .toLowerCase()
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    return formatted || responsibilityType;
};

// Function to format service dates
const formatServiceDates = (fromDos: string | undefined, throughDos: string | undefined): string => {
    const from = fromDos ? formatStatementDate(fromDos) : null;
    const through = throughDos ? formatStatementDate(throughDos) : null;

    if (from && through && from !== through) {
        return `${from} – ${through}`;
    } else if (from) {
        return from;
    } else {
        return '—';
    }
};

// Function to format patient name
const formatPatientName = (first: string | undefined, last: string | undefined): string => {
    if (first && last) {
        return `${first} ${last}`;
    } else if (first) {
        return first;
    } else if (last) {
        return last;
    } else {
        return '—';
    }
};

// Function to generate claims table rows
const generateClaimsTableRows = (claimsList: PatientDetails['claimsList']): string => {
    if (!claimsList || claimsList.length === 0) {
        return '<tr><td colspan="9" class="muted">No rows found</td></tr>';
    }

    // Sort by fromDos (service start date); if equal or missing, fall back to id
    const sorted = [...claimsList].sort((a, b) => {
        const aDate = a?.fromDos || '';
        const bDate = b?.fromDos || '';

        if (aDate && bDate && aDate !== bDate) {
            // Newest first
            return new Date(bDate).getTime() - new Date(aDate).getTime();
        }

        const aId = a?.id || '';
        const bId = b?.id || '';
        return aId.localeCompare(bId);
    });

    let allRows = '';
    let rowIndex = 0;
    let lastClaimId = '';

    sorted.forEach((claim, index) => {
        const currentClaimId = claim.id || '';
        const isNewClaimGroup = currentClaimId !== lastClaimId;

        // Add separator when claim ID changes (except for first row)
        if (isNewClaimGroup && index > 0) {
            allRows += `
        <tr class="date-separator">
          <td colspan="9" class="separator-cell"></td>
        </tr>
      `;
        }

        const isAltRow = rowIndex % 2 === 1;
        const rowClass = isAltRow ? 'row-alt' : '';

        allRows += `
      <tr class="${rowClass}">
        <td>${escapeHtml(claim.id) || '—'}</td>
        <td>${escapeHtml(formatResponsibility(claim.claimResponsibilityType))}</td>
        <td>${escapeHtml(formatServiceDates(claim.fromDos, claim.throughDos))}</td>
        <td>${escapeHtml(formatPatientName(claim.patientFirst, claim.patientLast))}</td>
        <td>${escapeHtml(claim.primaryPayerName) || '—'}</td>
        <td class="right">${claim.chargeAmount ? formatCurrency(claim.chargeAmount.amount, claim.chargeAmount.currency) : '—'}</td>
        <td class="right">${claim.allowedAmount ? formatCurrency(claim.allowedAmount.amount, claim.allowedAmount.currency) : '—'}</td>
        <td class="right">${claim.payerAmount ? formatCurrency(claim.payerAmount.amount, claim.payerAmount.currency) : '—'}</td>
        <td class="right">${claim.patientAmount ? formatCurrency(claim.patientAmount.amount, claim.patientAmount.currency) : '—'}</td>
      </tr>
    `;

        lastClaimId = currentClaimId;
        rowIndex++;
    });

    return allRows;
};

// Returns unescaped provider info text; escape only at final HTML interpolation.
const buildProviderInfoText = (statement: any): string => {
    if (statement?.renderingProviders && statement.renderingProviders.length > 0) {
        return (
            'Provider(s): ' +
            statement.renderingProviders
                .filter((provider: any) => provider?.firstName && provider?.lastName)
                .map((provider: any) => `${provider.firstName}, ${provider.lastName}`)
                .join(', ')
        );
    }
    return '';
};

const buildBillingFacilityColumn = (statement: PatientDetails['statementsList'][0]): string => {
    const fmt = (label: string, value: string | undefined) => {
        const v = value !== undefined && value !== null && String(value).trim() !== '' ? String(value).trim() : '—';
        return `${escapeHtml(label)}: ${escapeHtml(v)}`;
    };
    return [
        fmt('Facility', statement?.facility),
        fmt('Billing NPI', statement?.billingNpi),
        fmt('Rendering NPI', statement?.renderingNpi),
        fmt('EIN', statement?.ein),
    ].join('<br/>');
};

const renderDiagnosisTags = (diagnosisCodes: string[] | undefined): string => {
    if (!diagnosisCodes?.length) return '';
    const codes = diagnosisCodes.filter(code => code != null && String(code).trim() !== '');
    if (!codes.length) return '';
    const tags = codes
        .map((code: string) => `<span class="badge ${getBadgeColor(code)}">${escapeHtml(String(code).trim())}</span>`)
        .join('');
    return `<div class="diagnosis-tags"><span class="diagnosis-label">Diagnosis: </span>${tags}</div>`;
};

// Function to get badge color for diagnosis code
const getBadgeColor = (code: string): string => {
    // Create a more robust hash from the code to get consistent colors
    if (!code) {
        return 'blue'; // default color
    }

    let hash = 0;
    for (let i = 0; i < code.length; i++) {
        const char = code.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32-bit integer
    }

    // Add some additional mixing for better distribution
    hash = hash ^ (hash >>> 16);
    hash = hash * 0x85ebca6b;
    hash = hash ^ (hash >>> 13);
    hash = hash * 0xc2b2ae35;
    hash = hash ^ (hash >>> 16);

    // Use absolute value and modulo to get a positive number
    const colorIndex = Math.abs(hash) % 6;

    const colors = [
        'yellow', // Color 1
        'green', // Color 2
        'blue', // Color 3
        'purple', // Color 4
        'orange', // Color 5
        'pink', // Color 6
    ];

    return colors[colorIndex];
};

// Function to generate statements table rows
const generateStatementsTableRows = (statementsList: PatientDetails['statementsList']): string => {
    let allRows = '';
    let rowIndex = 0;
    let lastDate = '';

    statementsList.forEach((statement, index) => {
        const currentDate = statement.date;
        const isNewDateGroup = currentDate !== lastDate;
        const isFirstInGroup = isNewDateGroup;

        // Add date group separator (except for first group)
        if (isNewDateGroup && index > 0) {
            allRows += `
        <tr class="date-separator">
          <td colspan="6" class="separator-cell"></td>
        </tr>
      `;
        }

        const isAltRow = rowIndex % 2 === 0;
        const rowClass = isAltRow ? 'row-alt' : '';

        const billingFacilityCell = buildBillingFacilityColumn(statement);
        const diagnosisTagsHtml = renderDiagnosisTags(statement.diagnosisCodes);

        if (statement.type === 'claim') {
            allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${escapeHtml(formatStatementDate(statement.date))}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong> <span class="muted">(${escapeHtml(statement.description)?.includes('Pending') ? 'Pending Adjudication' : ''})</span></div>
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        } else if (statement.type === 'line') {
            const providerInfoText = buildProviderInfoText(statement);
            allRows += `
        <tr class="${rowClass}">
          <td></td>
          <td>
            <div>${escapeHtml(statement.description) || '—'}</div>
            ${providerInfoText ? `<div class="muted provider-info">${escapeHtml(providerInfoText)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        } else if (statement.type === 'allocated') {
            const providerInfoText = buildProviderInfoText(statement);
            allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? escapeHtml(formatStatementDate(statement.date)) : ''}</td>
          <td>
            <div>${escapeHtml(statement.description) || '—'}</div>
            ${providerInfoText ? `<div class="muted provider-info">${escapeHtml(providerInfoText)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        } else if (statement.type === 'unallocated') {
            const providerInfoText = buildProviderInfoText(statement);
            allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? escapeHtml(formatStatementDate(statement.date)) : ''}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong></div>
            ${providerInfoText ? `<div class="muted provider-info">${escapeHtml(providerInfoText)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        } else if (statement.type === 'adjustment') {
            const providerInfoText = buildProviderInfoText(statement);
            allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? escapeHtml(formatStatementDate(statement.date)) : ''}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong></div>
            ${providerInfoText ? `<div class="muted provider-info">${escapeHtml(providerInfoText)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        } else if (statement.type === 'payment') {
            const providerInfoText = buildProviderInfoText(statement);
            allRows += `
        <tr class="${rowClass} ${isFirstInGroup ? 'date-group-first' : ''}">
          <td>${isFirstInGroup ? escapeHtml(formatStatementDate(statement.date)) : ''}</td>
          <td>
            <div><strong>${escapeHtml(statement.description) || '—'}</strong></div>
            ${providerInfoText ? `<div class="muted provider-info">${escapeHtml(providerInfoText)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        } else if (statement.type === 'balance') {
            const providerInfoText = buildProviderInfoText(statement);
            allRows += `
        <tr class="${rowClass}">
          <td></td>
          <td>
            <div class="row-note">${escapeHtml(statement.description) || '—'}</div>
            ${providerInfoText ? `<div class="muted provider-info">${escapeHtml(providerInfoText)}</div>` : ''}
            ${diagnosisTagsHtml}
          </td>
          <td>${billingFacilityCell}</td>
          <td class="right">${statement.charge ? formatCurrency(statement.charge) : '—'}</td>
          <td class="right">${statement.credit ? formatCurrency(statement.credit) : '—'}</td>
          <td class="right">${statement.balance ? formatCurrency(statement.balance) : '—'}</td>
        </tr>
      `;
        }

        lastDate = currentDate;
        rowIndex++;
    });

    return allRows;
};

const PDF_STYLES = `
    :root{
      --bg:${Variables.ThemeLight.Background1};
      --panel:${Variables.Swatches.White};
      --text:${Variables.Colors.n100};
      --muted:${Variables.Colors.n70};
      --line:${Variables.Colors.n30};
      --blue:${Variables.Colors.b300};
      --blue-100:${Variables.Colors.p60};
      --green:${Variables.Swatches.Green6};
      --yellow:${Variables.Colors.amber30};
      --chip-bg:${Variables.Colors.n5};
      --chip-border:${Variables.Colors.n30};
      --white:${Variables.Swatches.White};
      --title:${Variables.Colors.p500};
      --thead-bg:${Variables.Colors.ghostWhite};
      --thead-text:${Variables.Colors.n85};
      --row-separator:${Variables.ThemeLight.Background2};
      --row-alt:${Variables.Colors.p50};
      --print-border:${Variables.Colors.n30};
      --shadow-light:${Variables.Swatches.White};
      --badge-yellow-bg:${Variables.Colors.amber10};
      --badge-yellow-border:${Variables.Colors.amber20};
      --badge-green-bg:${Variables.Swatches.Green2};
      --badge-green-border:${Variables.Swatches.Green4};
      --badge-blue-bg:${Variables.Colors.b300_l90};
      --badge-blue-border:${Variables.Colors.b300_l76};
      --badge-purple-bg:${Variables.Colors.i10};
      --badge-purple-border:${Variables.Colors.i20};
      --badge-orange-bg:${Variables.Swatches.Orange3};
      --badge-orange-border:${Variables.Swatches.Orange5};
      --badge-pink-bg:${Variables.Swatches.Red2};
      --badge-pink-border:${Variables.Colors.r20};
    }
    *{box-sizing:border-box}
    body{
      margin:0;
      padding:0;
      background:var(--bg);
      color:var(--text);
      font:14px/1.4 system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji","Segoe UI Emoji";
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .container{max-width:100%;margin:0;padding:0 5px}
    .card{background:var(--panel);border:1px solid var(--line);border-radius:8px;box-shadow:0 1px 2px ${Variables.Colors.nx4}}
    .header{
      display:flex;align-items:center;justify-content:space-between;
      padding:12px 16px;border:1px solid var(--line);border-radius:8px;background:var(--white);margin-bottom:12px
    }
    .header-left{display:flex;align-items:center;gap:18px}
    .title{font-weight:700;font-size:20px;color:var(--title)}
    .big-amount{font-size:36px;font-weight:800}
    .actions{display:flex;gap:10px}
    .btn{border:1px solid var(--line);background:var(--white);border-radius:8px;padding:10px 14px;font-weight:600;cursor:pointer}
    .btn.primary{background:var(--blue);color:var(--white);border-color:var(--blue)}
    .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:0;width:100%}
    .panel{padding:12px}
    .panel h3{margin:0 0 10px 0;font-size:14px;text-transform:uppercase;letter-spacing:.04em;color:var(--title);font-weight:600}
    .muted{color:var(--muted)}
    .kv{display:grid;grid-template-columns:80px 1fr;row-gap:6px;column-gap:10px}
    .pill{display:inline-flex;align-items:center;gap:8px;background:var(--blue-100);color:var(--blue);border-radius:999px;padding:4px 10px;font-weight:700}
    .mini-btn{font-size:12px;padding:2px 8px;border-radius:6px;border:1px solid var(--line);background:var(--white);cursor:pointer}
    .tiny{font-size:12px}
    .table{width:100%;border-collapse:separate;border-spacing:0;margin-top:8px}
    .table th, .table td{padding:10px 12px;border-top:1px solid var(--line);vertical-align:top}
    .table th{background:var(--thead-bg);font-weight:700;color:var(--thead-text);text-align:left}
    .table tr:first-child th{border-top:none}
    .table tr:first-child td{border-top:none}
    .table .right{text-align:right;white-space:nowrap}
    .row-note{font-size:12px;color:var(--muted)}
    .badge{display:inline-block;padding:2px 8px;border-radius:6px;background:var(--white);border:1px solid var(--chip-border);margin-right:6px;margin-left:8px}
    .badge.yellow{background:var(--badge-yellow-bg);border-color:var(--badge-yellow-border)}
    .badge.green{background:var(--badge-green-bg);border-color:var(--badge-green-border)}
    .badge.blue{background:var(--badge-blue-bg);border-color:var(--badge-blue-border)}
    .badge.purple{background:var(--badge-purple-bg);border-color:var(--badge-purple-border)}
    .badge.orange{background:var(--badge-orange-bg);border-color:var(--badge-orange-border)}
    .badge.pink{background:var(--badge-pink-bg);border-color:var(--badge-pink-border)}
    .date-separator{background-color:var(--row-separator) !important}
        .date-group-first{border-top:1px solid var(--line)}
    .separator-cell{padding:8px 0;border-top:2px solid var(--line);background-color:var(--row-separator)}
    .summary-row{border-top:2px solid var(--line);background-color:var(--row-separator);font-weight:700}
    .summary-label{text-align:right;padding:12px 8px}
    .summary-value{padding:12px 8px}
    .diagnosis-tags{margin-top:6px;display:block;line-height:1.4}
    .diagnosis-label{font-size:11px;color:var(--muted)}
    .provider-info{margin-top:4px;font-size:12px}
    .statement-card{margin-top:8px}
    .col-date{width:120px}
    .col-billing{width:240px}
    .col-amount{width:120px}
    .section-title{padding:14px 16px;border-bottom:1px solid var(--line);font-weight:700}
    .toolbar{padding:10px 16px;border-top:1px solid var(--line);display:flex;justify-content:flex-end;gap:8px}
    .shadowed{box-shadow:inset 0 1px 0 var(--shadow-light)}
    .subtle{color:var(--title)}
    .row-alt{background:var(--row-alt)}
    .grid-2 > *{min-width:0;overflow:hidden}
    @media (max-width:600px){
      .container{padding:0 8px}
      .grid-2{grid-template-columns:1fr;gap:12px}
      .kv{grid-template-columns:80px 1fr}
      .header{flex-direction:column;align-items:flex-start;gap:8px;padding:12px 16px;margin-bottom:12px}
      .actions{align-self:flex-end}
      .panel{padding:12px}
    }
    @media print {
      .container{padding:0 2px}
      .card{box-shadow:none;border:1px solid var(--print-border);margin-bottom:3px;page-break-inside:avoid;orphans:3;widows:3}
      .btn{display:none}
      .header{padding:6px 10px;margin-bottom:6px;page-break-inside:avoid;page-break-after:avoid}
      .panel{padding:6px;page-break-inside:avoid}
      .grid-2{display:grid !important;grid-template-columns:1fr 1fr !important;gap:8px;page-break-inside:avoid;width:100%;orphans:3;widows:3}
      .kv{grid-template-columns:70px 1fr;row-gap:4px;column-gap:8px}
      .section-title{padding:6px 12px;margin-bottom:0;page-break-after:avoid}
      .table{page-break-inside:auto;margin-top:0;break-inside:auto}
      .table th, .table td{padding:4px 8px;page-break-inside:avoid}
      .table tr{page-break-inside:avoid;page-break-after:auto;orphans:2;widows:2}
      .table thead{display:table-header-group}
      .table tbody tr:first-child{page-break-before:avoid}
      .card + .card{margin-top:6px !important}
      .badge{page-break-inside:avoid}
      .row-note{page-break-inside:avoid}
    }
  .page-break-before{page-break-before: always; break-before: page;}
`;

const buildAddressHtml = (
    address:
        | {
              lines?: string[];
              city?: string;
              state?: string;
              postal?: string;
              country?: string;
          }
        | null
        | undefined,
): string => {
    if (!address) {
        return '—';
    }

    const lines = (address.lines || [])
        .map(line => escapeHtml(String(line || '').trim()))
        .filter(Boolean)
        .join('<br/>');

    const city = escapeHtml(String(address.city || '').trim());
    const state = escapeHtml(String(address.state || '').trim());
    const postal = escapeHtml(String(address.postal || '').trim());
    const country = escapeHtml(String(address.country || '').trim());

    const statePostal = [state, postal].filter(Boolean).join(' ');
    const cityStatePostal = [city, statePostal].filter(Boolean).join(city && statePostal ? ', ' : '');
    const cityStatePostalCountry = [cityStatePostal, country].filter(Boolean).join(' ');

    return [lines, cityStatePostalCountry].filter(Boolean).join('<br/>') || '—';
};

const buildHeaderSection = (balanceAmountHtml: string): string => `
  <div class="header card">
    <div class="header-left">
      <div>
        <div class="title">Current Balance</div>
        <div class="big-amount">${balanceAmountHtml}</div>
      </div>
    </div>
  </div>
`;

const buildPatientAndBillingSection = (
    patient: PatientDetails['statement']['patient'] | undefined,
    billingProvider: PatientDetails['statement']['billingProvider'] | undefined,
): string => {
    const patientAddress = patient?.addresses?.length ? patient.addresses[0] : null;
    const billingAddress = billingProvider?.addresses?.length ? billingProvider.addresses[0] : null;

    const patientName =
        escapeHtml(`${patient?.first || ''} ${patient?.middle || ''} ${patient?.last || ''}`.trim()) || '—';

    return `
    <div class="grid-2">
      <div class="card panel">
        <h3>Patient</h3>
        <div class="kv">
          <div>Name</div><div><strong>${patientName}</strong> <span class="badge">${escapeHtml(patient?.sex) || '—'}</span></div>
          <div>DOB</div><div>${escapeHtml(formatStatementDate(patient?.dob || ''))}</div>
          <div>Phone</div><div>${escapeHtml(patient?.phones?.[0]) || '—'}</div>
          <div>Email</div><div><a href="mailto:${escapeHtml(patient?.emails?.[0]) || ''}">${escapeHtml(patient?.emails?.[0]) || '—'}</a></div>
          <div>Address</div><div>${buildAddressHtml(patientAddress)}</div>
          <div>External ID</div><div>${escapeHtml(patient?.externalId) || '—'}</div>
        </div>
      </div>
      <div class="card panel">
        <h3>Billing Provider</h3>
        <div class="kv">
          <div>Organization</div><div><strong>${escapeHtml(billingProvider?.organization) || '—'}</strong></div>
          <div>Phone</div><div>${escapeHtml(billingProvider?.phone) || '—'}</div>
          <div>Email</div><div><a href="mailto:${escapeHtml(billingProvider?.email) || ''}">${escapeHtml(billingProvider?.email) || '—'}</a></div>
          <div>Address</div><div>${buildAddressHtml(billingAddress)}</div>
          <div>EIN</div><div>${escapeHtml(billingProvider?.ein) || '—'}</div>
          <div>NPI</div><div>${escapeHtml(billingProvider?.npi) || '—'}</div>
        </div>
      </div>
    </div>
  `;
};

const buildClaimsSection = (claimsList: PatientDetails['claimsList'] | undefined): string => `
    <div class="card statement-card">
      <div class="section-title">Claims</div>
      <div class="panel">
        <table class="table">
          <thead>
            <tr>
              <th>Claim ID</th>
              <th>Responsibility</th>
              <th>Service Dates</th>
              <th>Patient Name</th>
              <th>Payer</th>
              <th class="right">Total Charge</th>
              <th class="right">Payer Allowed</th>
              <th class="right">Payer Paid</th>
              <th class="right">Patient Amount</th>
            </tr>
          </thead>
          <tbody>
            ${generateClaimsTableRows(claimsList || [])}
          </tbody>
        </table>
      </div>
    </div>
`;

const buildPaymentsSection = (paymentsList: PatientDetails['paymentsList']): string => `
    <div class="card statement-card">
      <div class="section-title">Payments</div>
      <div class="panel">
        <table class="table">
          <thead>
            <tr>
              <th>Payment date</th>
              <th class="right">Total amount</th>
              <th class="right">Unallocated amount</th>
              <th class="right">Allocated amount</th>
              <th class="right">Refund amount</th>
            </tr>
          </thead>
          <tbody>
            ${generatePaymentsTableRows(paymentsList || [])}
          </tbody>
        </table>
      </div>
    </div>
`;

const renderFullDocument = (
    headerHtml: string,
    patientAndBillingHtml: string,
    claimsHtml: string,
    paymentsHtml: string,
    statementSectionHtml: string,
): string => `
<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Patient Statement - Enter Health</title>
  <style>${PDF_STYLES}</style>
</head>
<body>
  <div class="container">
    ${headerHtml}
    ${patientAndBillingHtml}
    ${claimsHtml}
    ${paymentsHtml}
    ${statementSectionHtml}
  </div>
</body>
</html>`;

// Data transformation function to convert from React component data structures to PDF format
export const transformDataForPDF = (
    patientStatement: any,
    paymentsData: any[],
    statementData: any[],
    claimsData?: any[],
): PatientDetails => {
    return {
        statement: patientStatement,
        paymentsList: (paymentsData || []).map(payment => ({
            id: payment?.id || '',
            totalAmount: payment?.totalAmount || { amount: '0', currency: 'USD' },
            unallocatedAmount: payment?.unallocatedAmount || { amount: '0', currency: 'USD' },
            allocatedAmount: payment?.allocatedAmount || { amount: '0', currency: 'USD' },
            externalTransactionId: payment?.externalTransactionId || '',
            paymentDate: payment?.paymentDate || '',
            refundAmount: payment?.refundAmount || { amount: '0', currency: 'USD' },
        })),
        statementsList: (statementData || []).map(statement => {
            // Find the claim to get rendering providers and facility/npi/ein
            const claim = patientStatement?.claims?.find((c: any) => c?.id === statement?.claimId);
            const billingProvider = patientStatement?.billingProvider;
            const facilityName =
                claim?.facility?.name ?? (typeof claim?.facility === 'string' ? claim?.facility : undefined);
            const renderingNpis = (claim?.renderingProviders || []).map((p: any) => p?.npi).filter(Boolean);
            const renderingNpi = renderingNpis.length > 0 ? renderingNpis.join(', ') : undefined;

            return {
                id: statement?.id || '',
                type: statement?.type || '',
                date: statement?.date || '',
                description: statement?.description || '',
                charge: statement?.charge?.amount || '0',
                credit: statement?.credit?.amount || '0',
                balance: statement?.balance?.amount || '0',
                diagnosisCodes: statement?.diagnosisCodes || [],
                renderingProviders: claim?.renderingProviders || [],
                facility: facilityName,
                billingNpi: claim?.billingNpi ?? claim?.npi ?? billingProvider?.npi,
                renderingNpi,
                ein: claim?.ein ?? billingProvider?.ein,
                metadata: statement?.metadata,
            };
        }),
        claimsList: (claimsData || []).map(claim => ({
            id: claim?.id || '',
            claimResponsibilityType: claim?.claimResponsibilityType || undefined,
            fromDos: claim?.fromDos || undefined,
            throughDos: claim?.throughDos || undefined,
            patientFirst: claim?.patientFirst || undefined,
            patientLast: claim?.patientLast || undefined,
            primaryPayerName: claim?.primaryPayerName || undefined,
            chargeAmount: claim?.chargeAmount || undefined,
            allowedAmount: claim?.allowedAmount || undefined,
            payerAmount: claim?.payerAmount || undefined,
            patientAmount: claim?.patientAmount || undefined,
        })),
    };
};

export type StatementTotals = { totalCharge: number; totalCredit: number; totalBalance: number };

const parseMoney = (val: any): number => {
    if (val === null || val === undefined) return 0;
    if (typeof val === 'number') return val;
    const s = String(val).trim();
    if (!s || s === '—') return 0;
    const num = parseFloat(s.replace(/[$,]/g, ''));
    return Number.isFinite(num) ? num : 0;
};

export const computeStatementTotals = (statementsList: PatientDetails['statementsList']): StatementTotals => {
    let totalCharge = 0;
    let totalCredit = 0;
    let totalBalance = 0;

    for (const row of statementsList || []) {
        totalCharge += parseMoney((row as any).charge);
        totalCredit += parseMoney((row as any).credit);
        totalBalance += parseMoney((row as any).balance);
    }

    return { totalCharge, totalCredit, totalBalance };
};

const renderStatementTable = (
    rowsHtml: string,
    title: string,
    opts: { showTotalRow: boolean; totals?: StatementTotals },
): string => {
    const { showTotalRow, totals } = opts;
    const headerHtml = title ? `<div class="section-title">${escapeHtml(title)}</div>` : '';

    const totalRow =
        showTotalRow && totals
            ? `
      <tr class="summary-row">
        <td colspan="3" class="summary-label">Total:</td>
        <td class="right summary-value">${formatCurrency(totals.totalCharge.toString())}</td>
        <td class="right summary-value">${formatCurrency(totals.totalCredit.toString())}</td>
        <td class="right summary-value">${formatCurrency(totals.totalBalance.toString())}</td>
      </tr>
    `
            : '';

    return `
    <div class="card statement-card">
      ${headerHtml}
      <div class="panel">
        <table class="table">
          <thead>
            <tr>
              <th class="col-date">Date</th>
              <th>Description</th>
              <th class="col-billing">Billing & facility</th>
              <th class="right col-amount">Charge</th>
              <th class="right col-amount">Credit</th>
              <th class="right col-amount">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${rowsHtml}
            ${totalRow}
          </tbody>
        </table>
      </div>
    </div>
  `;
};

// Function to generate HTML from patient details (single-table version, kept for compatibility)
export const generateHTML = (data: PatientDetails): string => {
    const { statementsList } = data;
    const totals = computeStatementTotals(statementsList || []);
    return generateHTMLForStatementChunk(data, {
        showTotalRow: true,
        totals,
        showTitle: true,
    });
};

export const generateHTMLForStatementChunk = (
    data: PatientDetails,
    opts: { showTotalRow: boolean; totals: StatementTotals; showTitle?: boolean },
): string => {
    const { statement, paymentsList, statementsList, claimsList } = data;
    const { patient, billingProvider, balanceDue } = statement || {};

    const statementTitle = opts.showTitle === false ? '' : 'Statement';
    const statementSection = renderStatementTable(generateStatementsTableRows(statementsList || []), statementTitle, {
        showTotalRow: opts.showTotalRow,
        totals: opts.totals,
    });

    return renderFullDocument(
        buildHeaderSection(formatCurrency(balanceDue?.amount || '0', balanceDue?.currency || 'USD')),
        buildPatientAndBillingSection(patient, billingProvider),
        buildClaimsSection(claimsList || []),
        buildPaymentsSection(paymentsList || []),
        statementSection,
    );
};
